/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/common/Utilities" />
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { Selection } from "OfficeFabric/Selection";
import { equals, localeFormat } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution"
import { IVssPageService } from "VSS/Platform/Page";
import { ServiceEndpoint as DistributedTaskServiceEndpoint } from "Wit/Clients/DistributedTask/Contracts/TaskAgent";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { ServiceEndpoint, GitHubConfigurationSettingsResponse } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";

export function convertServiceEndpointToViewModel(serviceEndpoint: DistributedTaskServiceEndpoint): ServiceEndpoint {
    return {
        id: serviceEndpoint.id,
        name: serviceEndpoint.name,
        gitHubHandle: serviceEndpoint.data.GitHubHandle,
        type: serviceEndpoint.type,
        url: serviceEndpoint.url,
        authorizationScheme: serviceEndpoint.authorization.scheme,
        createdBy: serviceEndpoint.createdBy,
        isEndpointValid: true
    };
}

/**
 * Utility function for add/remove repo pickers to update selected repos list based on the selection changes from DetailsList.
 * Since selection will reset when filter applies, it is important to maintain an updated list of selected repos.
 *
 * See examples below.
 *
 * listedRepos:     [0:Repo1, 1:Repo2, 2:Repo3, 3:Repo4]
 * selectedIndices: [         0:1,              1:3    ]
 * selectedRepos:   {
 *                      [id1]:Repo1
 *                  }
 *
 * => selectedRepos:
 * {
 *      [Id2]:Repo2,
 *      [Id4]:Repo4
 * }
 */
export function updateSelectedRepos(
    listedRepos: ExternalGitRepo[],
    selectedRepos: { [repoNodeId: string]: ExternalGitRepo },
    selection: Selection
): void {
    const selectedIndices = selection.getSelectedIndices();

    let indiceIndex = 0;
    for (let repoIndex = 0; repoIndex < listedRepos.length; repoIndex++) {
        const repo = listedRepos[repoIndex];

        if (indiceIndex < selectedIndices.length) {
            const indice = selectedIndices[indiceIndex];
            if (repoIndex === indice) {
                // Select the repo and move to next indice.
                selectedRepos[repo.id] = repo;
                indiceIndex++;
            } else if (selectedRepos[repo.id]) {
                // Clear the selection if the repo was marked as selected.
                delete selectedRepos[repo.id];
            }
        } else if (selectedRepos[repo.id]) {
            delete selectedRepos[repo.id];
        }
    }
}

// TODO: Move to core utilities.
export function isHosted(pageContext: IVssPageContext): boolean {
    const pageService = pageContext.getService<IVssPageService>("IVssPageService");
    // tslint:disable-next-line:no-non-null-assertion
    return pageService.getData()!.isHosted;
}

/**
 * Includes the friendly name of the repository type in the string to format.
 */
export function getTextWithServiceName(repositoryType: string, textFormat: string): string {
    const serviceName = equals(repositoryType, RepositoryTypes.GitHub, true) ? Resources.GitHub : Resources.GitHubEnterprise;
    return localeFormat(textFormat, serviceName);
}

export function getRepositoryZeroDataText(repositoryType: string, isGitHubApp: boolean): string {
    const isGitHubService = equals(repositoryType, RepositoryTypes.GitHub, true);

    if (isGitHubService && isGitHubApp) {
        return Resources.AddRepo_EmptyRepositoryInfoError;
    } else {
        return Resources.AddRepo_EmptyRepositoryInfoWarning;
    }
}

export function getMaxRepoCount(pageContext: IVssPageContext): number {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const repoSettings = contributionService.getData<GitHubConfigurationSettingsResponse>(
        GitHubConnectionConstants.GitHubConfigurationSettingsDataProviderId
    );
    return repoSettings ? repoSettings.maxRepoCount : 250;
}
